'use client';

import { forwardRef, useMemo, useState } from 'react';
import { Pipette } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import type { ButtonProps } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useForwardedRef } from '@/hooks/use-forwarded-ref';

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

const ColorPicker = forwardRef<
  HTMLInputElement,
  Omit<ButtonProps, 'value' | 'onChange' | 'onBlur'> & ColorPickerProps
>(
  (
    { disabled, value, onChange, onBlur, name, className, size, ...props },
    forwardedRef
  ) => {
    const ref = useForwardedRef(forwardedRef);
    const [open, setOpen] = useState(false);

    const parsedValue = useMemo(() => {
      return value || '#FFFFFF';
    }, [value]);

    const handleEyeDropper = async () => {
      // Check if EyeDropper API is supported
      if (!('EyeDropper' in window)) {
        alert('EyeDropper API is not supported in your browser. Please use Chrome/Edge 95+ or Safari 16+');
        return;
      }

      try {
        // @ts-ignore - EyeDropper is not in TypeScript lib yet
        const eyeDropper = new EyeDropper();
        const result = await eyeDropper.open();
        onChange(result.sRGBHex);
      } catch (error) {
        // User cancelled the eyedropper
        console.log('EyeDropper cancelled or failed:', error);
      }
    };

    return (
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild disabled={disabled} onBlur={onBlur}>
          <Button
            {...props}
            className={cn('block', className)}
            name={name}
            onClick={() => {
              setOpen(true);
            }}
            size={size}
            style={{
              backgroundColor: parsedValue,
            }}
            variant='outline'
          >
            <div />
          </Button>
        </PopoverTrigger>
        <PopoverContent side='right' className='w-full'>
          <div className='flex flex-col gap-3'>
            <HexColorPicker color={parsedValue} onChange={onChange} />
            <div className='flex gap-2 justify-center items-center'>
              <Input
                maxLength={7}
                onChange={(e) => {
                  onChange(e?.currentTarget?.value);
                }}
                ref={ref}
                value={parsedValue}
                className='flex-1'
              />
              <Button
                type='button'
                size='sm'
                variant='outline'
                onClick={handleEyeDropper}
                title='Pick color from page'
                className='px-2 flex items-center justify-center'
              >
                <Pipette className='h-3.5 w-3.5' />
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);
ColorPicker.displayName = 'ColorPicker';

export { ColorPicker };